// Setup assistant (SETUP_ASSISTANT_PLAN.md S1, S3, S4, S8–S11, §5b): the pure
// words and maths behind the entry points, the conversation page, the draft
// panel, the check page, the done page and the saved-proposals list. Nothing
// here talks to the API (that is composables/useSetupAssistant.js), and nothing
// the model wrote is ever turned into HTML: every string is rendered as text
// (SE3). Mirrors the API's SetupAssistantService wording where it has any.
import {formatMinor} from '~/utils/adminActionCopy';
import {bandSummary, cutoffWord, durationWord, AUDIENCE} from '~/utils/bookings';

// X249: Competitions is the competition entry points' focus (Create new / New competition / Manage competitions): no goals card, straight to which kind.
export const FOCUS = Object.freeze({fullSetup: 'FullSetup', memberships: 'Memberships', bookings: 'Bookings', competitions: 'Competitions'});
export const STEP_ACTION = Object.freeze({new: 'New', change: 'Change', remove: 'Remove'});
export const PROPOSAL_STATUS = Object.freeze({draft: 'Draft', ready: 'Ready', applied: 'Applied', undone: 'Undone', discarded: 'Discarded'});
export const SESSION_STATUS = Object.freeze({open: 'Open', applied: 'Applied', discarded: 'Discarded'});
export const ENTITY = Object.freeze({
  membershipType: 'MembershipType', playingArea: 'PlayingArea', areaGroup: 'AreaGroup', bookingSettings: 'BookingSettings',
  // X105 (C1): the five competition formats.
  league: 'League', ladder: 'Ladder', tournament: 'Tournament', americano: 'Americano', meet: 'Meet',
  // X132: the club's own scoring system, drafted from a template so a competition of the same proposal can use it.
  scoringSystem: 'ScoringSystem',
});

/** X132: a New ScoringSystem step (no form before it exists; the Scoring systems page lists it afterwards). */
export function isScoringSystemStep(step) {
  return String(step?.entityType ?? '') === ENTITY.scoringSystem;
}
export const COMPETITION_ENTITIES = Object.freeze([ENTITY.league, ENTITY.ladder, ENTITY.tournament, ENTITY.americano, ENTITY.meet]);
/** C4: the whole-club order is playing areas, competitions, memberships & passes, bookings. */
// X200: a club's own scoring system has its own section, before the competitions that use it (never under "Competitions").
export const SECTION_TITLES = Object.freeze({playingAreas: 'Playing areas', scoringSystems: 'Scoring systems', competitions: 'Competitions', memberships: 'Memberships & passes', bookings: 'Bookings'});
export const SECTION_ORDER = Object.freeze(['playingAreas', 'scoringSystems', 'competitions', 'memberships', 'bookings']);

export function isCompetitionStep(step) {
  return COMPETITION_ENTITIES.includes(String(step?.entityType ?? ''));
}

/** The format key the club home uses ("league", "ladder", "tournament", "americano", "meet") for a competition step. */
export function competitionFormatOf(entityType) {
  return String(entityType ?? '').toLowerCase();
}

/** "box league", "ladder", "tournament", "americano league", "meet" (the API's words). */
export function competitionWord(entityType) {
  switch (String(entityType ?? '')) {
    case ENTITY.league: return 'box league';
    case ENTITY.ladder: return 'ladder';
    case ENTITY.tournament: return 'tournament';
    case ENTITY.americano: return 'americano league';
    case ENTITY.meet: return 'meet';
    default: return 'competition';
  }
}

export const SOMETHING_ELSE = 'Something else…';
export const ASSISTANT_NAME = 'Setup assistant';
/** Under the composer on every conversation (AiStart). */
// X181: no longer a line under the composer (Grant: "no one will read it"); it opens the "How we use what you type" dialog
// from the conversation's More menu. Since X167 the assistant does use names typed to find or add players, so it says so.
export const PROPOSES_ONLY_LINE = "The assistant only proposes changes; nothing happens until you press Create or Apply. It only knows your club's set-up and the names you type to add players; it never sees members' contact or health details.";
/** The check page's footer for a first-time setup (AiReview) and for changes (ScenApply). */
export const CREATE_FOOTER = 'Nothing is visible to players yet: memberships are created unpublished and bookings stay off until you switch them on. A new competition is open to joining by its link as soon as it is created. You can undo the whole setup for 7 days.';

/**
 * X115 (R50 L1, L2): the create footer, honest about this proposal: a free membership a competition depends on is published at
 * apply (the API's publishedAtApply), and a competition that waits for a paid membership is not "open to joining by its link".
 * X118 (R52 L1): "Nothing is visible to players yet" only when that is true (something stays unpublished or off); a membership
 * that goes live when applied is said to; bookings are mentioned only when the proposal has booking steps.
 */
export function createFooter(proposal) {
  const steps = (proposal?.steps ?? []).filter(s => !s.keepLive);
  const published = steps.filter(s => s.entityType === ENTITY.membershipType && isNewStep(s) && s.publishedAtApply).map(s => s.label).filter(Boolean);
  const types = steps.filter(s => s.entityType === ENTITY.membershipType && isNewStep(s)).length;
  const bookings = steps.some(s => [ENTITY.areaGroup, ENTITY.bookingSettings].includes(s.entityType));
  const held = steps.filter(s => isCompetitionStep(s) && isNewStep(s) && s.joiningHeldFor);
  const competitions = steps.filter(s => isCompetitionStep(s) && isNewStep(s)).length;
  const hidden = [];
  if (types && published.length === types) { /* every membership goes live: nothing hidden on that side */ }
  else if (published.length) hidden.push(`memberships are created unpublished, except ${listWords(published)}, which ${published.length === 1 ? 'is' : 'are'} free and ${published.length === 1 ? 'goes' : 'go'} live when you apply`);
  else if (types) hidden.push('memberships are created unpublished');
  if (bookings) hidden.push('bookings stay off until you switch them on');
  const sentences = [];
  if (hidden.length) sentences.push(`Nothing is visible to players yet: ${hidden.join(' and ')}.`);
  if (types && published.length === types) sentences.push(`${listWords(published)} ${published.length === 1 ? 'is' : 'are'} free and ${published.length === 1 ? 'goes' : 'go'} live when you apply, so players can join what asks for ${published.length === 1 ? 'it' : 'them'} straight away.`);
  if (competitions && held.length === competitions) sentences.push(`${held.length === 1 ? 'The new competition' : 'The new competitions'} ${held.length === 1 ? 'opens' : 'open'} to joining once ${listWords([...new Set(held.map(s => s.joiningHeldFor))])} is published; until then ${held.length === 1 ? 'its' : 'their'} invite link stays off.`);
  else if (held.length) sentences.push(`A new competition is open to joining by its link as soon as it is created, except ${listWords(held.map(s => s.label))}, which waits for ${listWords([...new Set(held.map(s => s.joiningHeldFor))])} to be published.`);
  else if (competitions) sentences.push('A new competition is open to joining by its link as soon as it is created.');
  sentences.push('You can undo the whole setup for 7 days.');
  return sentences.join(' ');
}

/**
 * X118 (R52 L1): the Memberships & passes section note on the check page for an all-New proposal: a free membership a
 * competition depends on goes live when applied (publishedAtApply), so it is never "Created unpublished".
 */
export function membershipSectionNote(steps) {
  const types = (steps ?? []).filter(s => s.entityType === ENTITY.membershipType && isNewStep(s) && !s.keepLive);
  const published = types.filter(s => s.publishedAtApply);
  if (!types.length || !published.length) return 'Created unpublished';
  if (published.length === types.length) return published.length === 1 ? 'Goes live when you apply (free)' : 'Go live when you apply (free)';
  return `Created unpublished, except ${listWords(published.map(s => s.label).filter(Boolean))} (free, goes live when you apply)`;
}

/**
 * X118 (R52 M2): the done page's line when it has no next steps to show (an older API, or nothing to do): only what is true
 * of this proposal, so a published membership is never "to publish" and bookings are not mentioned without booking steps.
 * Null when there is nothing to say.
 */
export function doneFallback(proposal) {
  const steps = (proposal?.steps ?? []).filter(s => !s.keepLive && isNewStep(s));
  const memberships = steps.some(s => s.entityType === ENTITY.membershipType && !s.publishedAtApply);
  const bookings = steps.some(s => [ENTITY.areaGroup, ENTITY.bookingSettings].includes(s.entityType));
  if (!memberships && !bookings) return null;
  return {memberships, bookings};
}

/** "A", "A and B", "A, B and C" */
export function listWords(words) {
  const w = (words ?? []).filter(Boolean);
  if (w.length <= 1) return w[0] ?? '';
  return `${w.slice(0, -1).join(', ')} and ${w[w.length - 1]}`;
}

/**
 * X115 (R50 L1): the Competitions section's note on the check page for an all-New draft. X167 (C3): the assistant puts in the
 * players the admin named (matched in the club or added), so the note no longer says nobody is added; it says so when some are.
 */
export function competitionSectionNote(steps) {
  const fresh = (steps ?? []).filter(s => isCompetitionStep(s) && isNewStep(s) && !s.keepLive);
  const held = fresh.filter(s => s.joiningHeldFor);
  const tail = fresh.some(s => Array.isArray(s.players) && s.players.length) ? '; the players you named are put in' : '';
  if (fresh.length && held.length === fresh.length) return `Joining opens once ${listWords([...new Set(held.map(s => s.joiningHeldFor))])} is published${tail}`;
  if (held.length) return `Open to joining by their links once created, except ${listWords(held.map(s => s.label))} (waits for ${listWords([...new Set(held.map(s => s.joiningHeldFor))])})${tail}`;
  return `Open to joining by their links once created${tail}`;
}

/** X115 (R50 L1): the done page's heading over the competition rows: "Share the link(s)" only when a link is open to share. */
export function linksHeading(links) {
  const open = (links ?? []).filter(l => !l.heldFor).length;
  const held = (links ?? []).length - open;
  if (!open) return held === 1 ? 'The invite link' : 'The invite links';
  return open === 1 && !held ? 'Share the link' : 'Share the links';
}
export const APPLY_FOOTER = 'Applying changes the live setup in one go. If any part fails, nothing changes. You can undo it for 7 days if nobody has joined or paid since.';
/** S11, when the model is down (ScenResume). */
export const UNAVAILABLE_LINE = "The assistant isn't answering right now. Your draft is saved. Try again in a minute, or carry on in the forms: every item below opens in its form, already filled in.";
export const READ_ONLY_LINE = "You're viewing as the club. You can read and draft here, but only one of the club's own admins can apply, undo or set up payments.";
export const ANOTHER_ADMINS_LINE = 'Only the admin who drafted this can apply it. You can read it, and ask them to apply it.';
export const UNDO_DAYS = 7;
export const UNDO_LINE = 'Removes everything this setup created and puts changed settings back. Available for 7 days while nobody has joined, paid or booked.';
/** X121 (R55 L1): the conversation page once its applied proposal was undone; never "these changes are live". */
// X132 (R64 L4): the way back is the header's Start again, not the side menu.
export const UNDONE_SESSION_LINE = 'This setup was undone: what it created is gone and changed settings are back as they were. This conversation has ended; to set something up, press Start again in the ⋮ menu.';
export const DISCARDED_SESSION_LINE = 'This draft was discarded: nothing it drafted was applied and nothing live changed. This conversation has ended; to set something up, press Start again in the ⋮ menu.';
export const APPLIED_SESSION_LINE = 'These changes are live. This conversation has ended; to change something else, press Start again in the ⋮ menu.';

/**
 * X121 (R55 L1): how a conversation ended, read from the session and its proposal: 'undone' when either says so (an older API
 * leaves the session Applied after an undo, so the proposal wins), 'applied' when applied, '' while it is open or discarded.
 */
export function sessionEnded(session, proposal = null) {
  const s = String(session?.status ?? '').toLowerCase();
  const p = String(proposal?.status ?? '').toLowerCase();
  if (s === 'undone' || p === 'undone') return 'undone';
  if (s === 'applied' || p === 'applied') return 'applied';
  // X189: discarded (by any admin from History, X187) ends the conversation too
  if (s === 'discarded' || p === 'discarded') return 'discarded';
  return '';
}

/** X132 item 7: the side panel's list of what the assistant remembers about the club from earlier conversations. */
export const CLUB_NOTES_TITLE = 'What I remember about your club';
export const CLUB_NOTES_EMPTY = "Nothing yet. Lasting facts you tell me (where you play, what you never charge for, a house rule) are kept here for every later conversation; you can forget any.";

/** X129 (Grant 2026-10-26): the assistant speaks as Tap Leagues: the app's own icon (the manifest icon the app handoff page shows) and the brand's name. */
export const ASSISTANT_ICON = '/favicon/web-app-manifest-192x192.png';
export const ASSISTANT_SPEAKER = 'Tap Leagues';

/**
 * X129 (Grant 2026-10-26): the "Start again" confirm's words. An open conversation with a draft: the draft is discarded and
 * nothing live changes; one with nothing drafted yet: the conversation is thrown away; an applied one: what was applied stays
 * live (and can still be undone from its done page while it can); an undone one: nothing live changes.
 */
export function startAgainWords(ended, proposal = null, loadFailed = false) {
  // X132 (R64 L4): a conversation that could not be opened (another admin's, or an old link) is nobody's to throw away:
  // the viewer carries on in a conversation of their own (their open one of this kind, else a new one).
  if (loadFailed) return "The conversation that couldn't be opened is left as it is; nothing of anyone's is discarded. You carry on in a conversation of your own: your open one, if you have one, or a new one.";
  if (ended === 'applied') {
    const undo = proposal?.canUndo ? ' and can still be undone from its done page (See what changed)' : '';
    return `The setup you applied stays live${undo}. A new conversation starts from what the club has now.`;
  }
  if (ended === 'undone' || ended === 'discarded') return 'Nothing that\'s live changes. A new conversation starts from what the club has now.';
  if ((proposal?.steps ?? []).length) return 'Your draft is discarded; nothing that\'s live changes.';
  return 'This conversation is thrown away; nothing that\'s live changes.';
}

/**
 * X129: the assistant's avatar and name sit at the top of each RUN of assistant bubbles (Slack/iMessage style), never on
 * every bubble. The page's own opening bubble, when shown, is the first of the run the transcript's first assistant turn joins.
 */
export function startsAssistantRun(transcript, i, hasOpening = false) {
  const turns = transcript ?? [];
  const t = turns[i];
  if (!t || t.role === 'user') return false;
  if (i === 0) return !hasOpening;
  return turns[i - 1]?.role === 'user';
}

/**
 * X181 (Grant 2026-10-27: "Really slick messaging UI"): whether turn i is the LAST of its sender's run (the admin's turns
 * are one sender, the assistant's another). The run's avatar sits beside its last bubble, its time under it, and the gap
 * after it is the wider one between senders. `thinking` (the reply on its way) continues an assistant run.
 */
export function endsRun(transcript, i, thinking = false) {
  const turns = transcript ?? [];
  const t = turns[i];
  if (!t) return false;
  const next = turns[i + 1];
  if (!next) return !(thinking && t.role !== 'user');
  return (next.role === 'user') !== (t.role === 'user');
}

/**
 * X181: a run's time under its last bubble, 24h: "14:05" today, "Mon 26 Oct 14:05" on an earlier day. Empty when the
 * turn has no usable time (the page's own opening words).
 */
export function turnTimeLabel(at, now = new Date()) {
  if (!at) return '';
  const d = at instanceof Date ? at : new Date(/[zZ]|[+-]\d{2}:\d{2}$/.test(String(at)) ? at : `${at}Z`);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  if (sameDay) return time;
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
  return `${day} ${d.getDate()} ${month} ${time}`;
}

/** X181: the signed-in admin's initials for their avatar beside their own bubbles (as the messaging list draws one); "?" with no name. */
export function userInitials(user) {
  const first = String(user?.firstName ?? '').trim().charAt(0);
  const last = String(user?.lastName ?? '').trim().charAt(0);
  return (first + last).toUpperCase() || '?';
}

const GOALS_CARD_IDS = ['goals-confirm', 'goals-pick'];

/**
 * X121 (R55 M2): the question id typed words answer. The goals card is answered only by its Confirm (the ticked titles), so
 * words typed while it is open are an ordinary message to the model (no question id) and the goals stay as they were.
 */
export function typedQuestionId(question, text = '') {
  if (!question?.id) return null;
  // X204 (R83 N2): a settings card is answered only by its Confirm; words typed while it is open are the admin's message
  // (answering in words), sent with no question id, so the card is never confirmed by them and the words stay in the transcript
  if (isSettingsCard(question)) return null;
  if (GOALS_CARD_IDS.includes(String(question.id)) || (Array.isArray(question.goals) && question.goals.length > 0)) {
    // X122 (R57 L1): a typed short yes on the confirm card is its Yes (keeps the ticks shown), settled with no model call.
    return String(question.id) === 'goals-confirm' && isShortYes(text) ? 'goals-confirm' : null;
  }
  return question.id;
}

/** X122 (R57 L1): the words that mean yes on their own; the API's SetupGoalsCards.ShortYesWords keeps the same list. */
const SHORT_YES_WORDS = new Set([
  'yes', 'yep', 'yeah', 'yup', 'ya', 'yea', 'sure', 'ok', 'okay', 'k', 'correct', 'right', "that's right", 'thats right', 'that is right',
  "that's correct", 'looks good', 'sounds good', 'looks right', 'all good', 'perfect', 'spot on', 'confirm', 'confirmed', 'go ahead', 'y',
]);

/**
 * X122 (R57 L1): a short agreement ("yes", "yep", "Yes, that's right"), as the API reads it: 24 characters or fewer, and
 * either starting with yes / saying that's right or correct, or one of the yes words. Never a request ("Yes, add two courts and a league").
 */
export function isShortYes(text) {
  const t = String(text ?? '').trim();
  if (!t || t.length > 24) return false;
  const a = t.toLowerCase().replace(/\u2019/g, "'");
  if (a.startsWith('yes') || a.includes("that's right") || a.includes('thats right') || a.includes('correct') || a === 'right') return true;
  return SHORT_YES_WORDS.has(a.replace(/[.! ]+$/, ''));
}

const same = (a, b) => String(a ?? '').toLowerCase() === String(b ?? '').toLowerCase();

// ── Entry points (§1) ──

/** The focus a page's entry point starts: the Memberships page → memberships, Playing areas and Bookings → bookings, the dashboard → the whole club. */
export function focusWord(focus) {
  if (same(focus, FOCUS.memberships)) return 'memberships and passes';
  if (same(focus, FOCUS.bookings)) return 'bookings';
  if (same(focus, FOCUS.competitions)) return 'competitions';
  return 'your club';
}

/** "Set up with the assistant" until the section has something live, then "Change with the assistant". */
export function entryLabel(availability, focus) {
  const a = availability ?? {};
  const live = same(focus, FOCUS.memberships) ? !!a.hasMembershipTypes
      : same(focus, FOCUS.bookings) ? !!a.bookingsSetUp
          : same(focus, FOCUS.competitions) ? false
              : !!(a.hasMembershipTypes || a.bookingsSetUp || a.hasPlayingAreas);
  return live ? 'Change with the assistant' : 'Set up with the assistant';
}

/** The page title for a session from its proposal alone: Change once a step changes something live. */
export function sessionTitle(session) {
  const p = session?.proposal;
  const changing = (p?.steps ?? []).some(s => !same(s.action, STEP_ACTION.new));
  return changing ? 'Change with the assistant' : 'Set up with the assistant';
}

/**
 * The check and done pages' breadcrumb trail: the section the session was opened from (bookings sessions sit under
 * Playing areas › Bookings, the rest under Memberships & passes) and Change / Set up by what the proposal does (R4 Low 6).
 */
export function assistantCrumbs(proposal, clubId, {allNew = true, last = 'Check'} = {}) {
  const focus = proposalFocus(proposal);
  const bookings = focus === FOCUS.bookings;
  // X111 (R47 L6): a whole-club session sits under the dashboard as "Setup assistant" (the side menu's words), not under Memberships & passes.
  if (focus === FOCUS.fullSetup) {
    return [
      {title: 'Dashboard', to: {name: 'ClubDashboard', params: {clubId}}},
      {title: ASSISTANT_NAME, to: {name: 'ClubSetupAssistant', params: {clubId}, query: {focus: FOCUS.fullSetup, session: proposal?.sessionId}}},
      {title: last, disabled: true},
    ];
  }
  // X249: a competitions session sits under Competitions.
  if (focus === FOCUS.competitions) {
    return [
      {title: 'Dashboard', to: {name: 'ClubDashboard', params: {clubId}}},
      {title: 'Competitions', to: {name: 'ClubCompetitions', params: {clubId}}},
      {title: ASSISTANT_NAME, to: {name: 'ClubSetupAssistant', params: {clubId}, query: {focus: FOCUS.competitions, session: proposal?.sessionId}}},
      {title: last, disabled: true},
    ];
  }
  const section = bookings
      ? [{title: 'Playing areas', to: {name: 'ClubPlayingAreas', params: {clubId}}}, {title: 'Bookings', to: {name: 'ClubBookings', params: {clubId}}}]
      : [{title: 'Memberships & passes', to: {name: 'ClubMemberships', params: {clubId}}}];
  return [
    {title: 'Dashboard', to: {name: 'ClubDashboard', params: {clubId}}},
    ...section,
    {title: allNew ? 'Set up with the assistant' : 'Change with the assistant', to: {name: 'ClubSetupAssistant', params: {clubId}, query: {focus: bookings ? 'bookings' : undefined, session: proposal?.sessionId}}},
    {title: last, disabled: true},
  ];
}

/** One copy of each line, in order (the API and the step cards can carry the same sentence), R4 Low 5. */
export function dedupeLines(lines) {
  const seen = new Set();
  return (lines ?? []).filter((l) => { const key = typeof l === 'string' ? l : l?.text; if (seen.has(key)) return false; seen.add(key); return true; });
}

/** The check page's rows for the bookings an hours change would cancel, move or warn (R4 NM6). */
export function bookingChangeRows(change) {
  const p = change?.preview ?? {};
  const rows = (p.bookings ?? []).map((b) => ({
    id: b.id,
    text: b.line || [b.who, b.playingAreaName].filter(Boolean).join(' · '),
    outcome: String(b.outcome ?? ''),
  }));
  return {label: change?.label ?? '', message: p.message || '', rows};
}

/** The conversation page's title: the same words as the entry button the admin pressed (what the club has live decides), else the proposal's (TEST_T2 Low 22). A whole-club session is titled like the side menu's button, "Setup assistant" (X107 item 13, X111 M3). */
export function conversationTitle(availability, focus, session) {
  // X249: a competitions session is the same assistant, opened from a competition entry point.
  if (same(focus, FOCUS.fullSetup) || same(focus, FOCUS.competitions)) return ASSISTANT_NAME;
  return availability ? entryLabel(availability, focus) : sessionTitle(session);
}

/** X111 (R47 M3): the focus a proposal's session was opened with, so "Continue with the assistant" and "Back to the conversation" land in that session, not a memberships one. */
export function proposalFocus(proposal) {
  const f = String(proposal?.focus ?? '');
  if (same(f, FOCUS.bookings)) return FOCUS.bookings;
  if (same(f, FOCUS.fullSetup)) return FOCUS.fullSetup;
  if (same(f, FOCUS.competitions)) return FOCUS.competitions;
  return FOCUS.memberships;
}

/** X124 (R58 minor): each format's admin page for one competition; X144: its list is the Competitions page with the format picked. */
const COMPETITION_PAGES = Object.freeze({
  [ENTITY.league]: {page: 'ClubLeagueManage', param: 'leagueId', format: 'league'},
  [ENTITY.ladder]: {page: 'ClubLadderManage', param: 'ladderId', format: 'ladder'},
  [ENTITY.tournament]: {page: 'ClubTournamentManage', param: 'tournamentId', format: 'tournament'},
  [ENTITY.americano]: {page: 'ClubAmericanoManage', param: 'americanoId', format: 'americano'},
  [ENTITY.meet]: {page: 'ClubMeetManage', param: 'meetId', format: 'meet'},
});

/**
 * X124 (R58 minor): where the done page's "Your competitions" goes: the one competition the proposal created or changed
 * (its admin page), that format's list for several of one format, the club dashboard's competitions for a mix. What an
 * undone proposal created is archived, so it never leads there.
 */
export function competitionsRoute(proposal, clubId) {
  const dashboard = {name: 'ClubDashboard', params: {clubId}};
  const undone = same(proposal?.status, 'undone');
  const links = proposal?.competitionLinks ?? [];
  const comps = (proposal?.steps ?? []).filter(s => isCompetitionStep(s) && !s.keepLive).map((s) => ({
    entityType: String(s.entityType),
    id: isNewStep(s) ? (undone ? null : links.find(l => l.stepId === s.id)?.id ?? null) : s.targetId ?? null,
  })).filter(c => c.id || !undone);
  if (!comps.length) return dashboard;
  const pages = COMPETITION_PAGES[comps[0].entityType];
  if (!pages || comps.some(c => c.entityType !== comps[0].entityType)) return dashboard;
  if (comps.length === 1 && comps[0].id) return {name: pages.page, params: {clubId, [pages.param]: comps[0].id}};
  return {name: 'ClubCompetitions', params: {clubId}, query: {format: pages.format}};
}

/** X111 (R47 L6): the sections a proposal touched, as the done page's "Want to change something?" buttons: only what it dealt with. */
export function changeButtons(proposal, clubId) {
  const keys = new Set(sectionGroups(proposal).map(g => g.key));
  const out = [];
  if (keys.has('playingAreas')) out.push({title: 'Playing areas', to: {name: 'ClubPlayingAreas', params: {clubId}}});
  if (keys.has('competitions')) out.push({title: 'Your competitions', to: competitionsRoute(proposal, clubId)});
  if (keys.has('memberships')) out.push({title: 'Memberships & passes', to: {name: 'ClubMemberships', params: {clubId}}});
  if (keys.has('bookings')) out.push({title: 'Bookings', to: {name: 'ClubBookings', params: {clubId}}});
  return out;
}

/**
 * X111 (R47 M5): the model's words are text, never HTML (SE3), but a path it writes to one of the site's own pages
 * (/contact?subject=…, /admin/clubs/…, /invite/…) becomes a link. Only paths that start with a known root qualify;
 * the shown words for the contact page are "the contact page", for anything else the path without its query.
 */
const LINKABLE_ROOTS = ['/contact', '/admin/', '/clubs/', '/invite/', '/formats', '/features', '/leagues/', '/ladders/', '/tournaments/', '/americanos/', '/meets/'];
const PATH_RE = /\/[a-z][a-z0-9\-/]*(?:\?[^\s)\]"']*)?/gi;
export function linkedSegments(text) {
  const s = String(text ?? '');
  // X157 (Grant: "use bold text for bits that are the most important"): a **double-asterisk** span on one line is a bold
  // segment (the markers are not shown); links are found inside and outside it; a stray ** with no partner stays text.
  const out = [];
  let last = 0;
  for (const m of s.matchAll(BOLD_RE)) {
    if (m.index > last) out.push(...linkSegments(s.slice(last, m.index)));
    out.push(...linkSegments(m[1]).map(seg => ({...seg, bold: true})));
    last = m.index + m[0].length;
  }
  if (last < s.length || !out.length) out.push(...linkSegments(s.slice(last)));
  return out;
}

const BOLD_RE = /\*\*([^*\n]+?)\*\*/g;
const LIST_ITEM_RE = /^\s*[-•]\s+(.*)$/;

/**
 * X157: the assistant's words as blocks the component renders: a run of lines starting with "- " (or "• ") is a list (one
 * item per line, each item its own segments); everything else is a paragraph whose own line breaks are kept (pre-wrap).
 * Blank lines next to a list go, so a list never sits under an empty gap. Plain text with no list is one block, as before.
 */
export function assistantBlocks(text) {
  const lines = String(text ?? '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let para = [];
  const flush = () => {
    while (para.length && !para[0].trim()) para.shift();
    while (para.length && !para[para.length - 1].trim()) para.pop();
    if (para.length) blocks.push({list: false, segments: linkedSegments(para.join('\n'))});
    para = [];
  };
  for (const line of lines) {
    const item = LIST_ITEM_RE.exec(line);
    if (!item) { para.push(line); continue; }
    flush();
    const previous = blocks[blocks.length - 1];
    if (previous?.list) previous.items.push(linkedSegments(item[1]));
    else blocks.push({list: true, items: [linkedSegments(item[1])]});
  }
  flush();
  return blocks.length ? blocks : [{list: false, segments: linkedSegments('')}];
}

function linkSegments(s) {
  const out = [];
  let last = 0;
  for (const m of s.matchAll(PATH_RE)) {
    const start = m.index;
    const before = start === 0 ? '' : s[start - 1];
    // A path, not the tail of a word or a fraction ("3/4", "and/or"): what comes before is space, punctuation or the start.
    if (before && !/[\s(\[:,;]/.test(before)) continue;
    let path = m[0].replace(/[.,;:!?]+$/, '');
    if (!LINKABLE_ROOTS.some(r => path === r.replace(/\/$/, '') || path.startsWith(r))) continue;
    if (start > last) out.push({text: s.slice(last, start)});
    const bare = path.split('?')[0];
    // X115 (R50 L6): a competition's admin page (the model writes page:cN, the server the path) reads as words, never a path.
    const competitionPage = /^\/admin\/clubs\/[^/]+\/(leagues|ladders|tournaments|americanos|meets)\/[^/]+$/i.test(bare);
    out.push({text: bare === '/contact' ? 'the contact page' : competitionPage ? "the competition's page" : bare, to: path});
    last = start + path.length;
  }
  if (last < s.length) out.push({text: s.slice(last)});
  return out.length ? out : [{text: s}];
}

/** A brand-new club (ScenNewClub): nothing to play on, nothing to join, nothing to book. The dashboard offers "Set up your club". */
export function brandNew(availability) {
  const a = availability ?? {};
  return !!a.enabled && !a.hasPlayingAreas && !a.hasMembershipTypes && !a.bookingsSetUp;
}

/** The admin's open session with this focus, if any (the entry point then says Continue). */
export function openSessionFor(availability, focus) {
  return (availability?.openSessions ?? []).find(s => same(s.focus, focus)) ?? null;
}

/** Where "Use the forms instead" goes for a focus. */
export function formsRoute(focus, clubId) {
  if (same(focus, FOCUS.memberships)) return {name: 'ClubMemberships', params: {clubId}};
  if (same(focus, FOCUS.bookings)) return {name: 'ClubBookings', params: {clubId}};
  if (same(focus, FOCUS.competitions)) return {name: 'ClubCompetitions', params: {clubId}};
  return {name: 'ClubDashboard', params: {clubId}};
}

// ── The conversation (S3) ──

/** What the admin's answer says when they picked options and maybe typed too; "Something else…" alone sends nothing. */
export function answerText(picked = [], typed = '') {
  const chosen = (picked ?? []).filter(p => p && p !== SOMETHING_ELSE);
  const free = String(typed ?? '').trim();
  if (chosen.length && free) return `${chosen.join(', ')}. ${free}`;
  if (chosen.length) return chosen.join(', ');
  return free;
}

/** The model's own "Something else" / "Other" option: the page offers that itself, once (TEST_T2 Low 1). */
const SOMETHING_ELSE_LIKE = /^(?:something|anything)\s+(?:else|different)\b|^other(?:\s+\(.*\))?[.…]*$/i;
export function isSomethingElseOption(option) {
  const o = String(option ?? '').trim().replace(/[.…\s]+$/, '');
  return same(o, SOMETHING_ELSE.replace(/…$/, '')) || SOMETHING_ELSE_LIKE.test(o);
}

/** The question card's choices: the model's options (at most six, its own "something else" dropped) and "Something else…" once, when free text is allowed or the model offered it. */
export function questionChoices(question) {
  return questionChoiceRows(question).map(r => r.label);
}

/**
 * X132: the card's rows. A server-built pick card (question.choices: real records, each with its handle, labels unique, no cap)
 * sends the HANDLE when a row is clicked; any other card's rows are the model's options (at most six). The free-text row comes
 * last, once, as "Something else…" or the card's own words ("Another sport…" on the sport card).
 */
export function questionChoiceRows(question) {
  const picks = Array.isArray(question?.choices) ? question.choices.filter(c => c && String(c.label ?? '').trim()) : [];
  let rows;
  let offeredElse = false;
  if (picks.length) {
    rows = picks.map(c => ({label: String(c.label).trim(), handle: c.handle ? String(c.handle) : null, line: c.line ? String(c.line) : null, else: false}));
  } else {
    const raw = (question?.options ?? []).map(o => String(o ?? '').trim()).filter(Boolean);
    const kept = raw.filter(o => !isSomethingElseOption(o));
    offeredElse = raw.length !== kept.length;
    rows = kept.slice(0, 6).map(o => ({label: o, handle: null, line: null, else: false}));
  }
  if (question?.allowFreeText !== false || offeredElse) {
    const label = String(question?.freeTextLabel ?? '').trim() || SOMETHING_ELSE;
    rows.push({label, handle: null, line: null, else: true});
  }
  return rows;
}

/**
 * X138 (R68 M2): a multi-select card with many real choices (the whole-club sports card: every sport in the Sports table, common
 * first) is drawn as a compact tick grid with a filter box; nothing is ever cut to six.
 */
export const CHOICE_GRID_MIN = 9;
export function isChoiceGrid(question) {
  return !!question?.multiSelect && Array.isArray(question?.choices) && question.choices.filter(c => c && String(c.label ?? '').trim()).length >= CHOICE_GRID_MIN;
}
/** The grid's rows that match the filter box (the free-text row always stays). */
export function filterChoiceRows(rows, filter) {
  const f = String(filter ?? '').trim().toLowerCase();
  if (!f) return rows ?? [];
  return (rows ?? []).filter(r => r.else || String(r.label ?? '').toLowerCase().includes(f));
}

/**
 * X138 (R69): the availability's reason code, as the page's sentence. The API answers notAdmin / globalOff / modelOff / clubOff;
 * the raw code is never shown.
 */
const AVAILABILITY_REASONS = Object.freeze({
  clubOff: "The setup assistant isn't switched on for this club yet. Ask the Tap Leagues team to switch it on.",
  globalOff: "The setup assistant is switched off at the moment. Use the forms instead.",
  modelOff: "The setup assistant is switched off at the moment. Use the forms instead.",
  notAdmin: 'Only an admin of this club can use the setup assistant.',
});
export function availabilityReasonLine(reason) {
  const key = String(reason ?? '').trim();
  return AVAILABILITY_REASONS[key] ?? (key && !/^[a-z][A-Za-z]*$/.test(key) ? key : "The setup assistant isn't available for this club right now. Use the forms instead.");
}

/** X132: a server-built settings card (every rule of a competition's form, pre-filled, one Confirm). */
export function isSettingsCard(question) {
  return Array.isArray(question?.settings) && question.settings.length > 0 && !!question.stepId;
}

/**
 * X132: a settings card's Confirm payload: every row's value by key, as the API reads it: numbers as numbers (an empty optional
 * row as null), booleans as booleans, a select's value (handle), money typed in pounds sent in minor units; a dependent row whose
 * parent does not hold its value is sent as null.
 */
export function settingsValuesFor(rows, values) {
  const out = {};
  for (const r of rows ?? []) {
    const raw = values?.[r.key];
    const parentOk = !r.dependsOn || String(values?.[r.dependsOn] ?? '') === String(r.dependsValue ?? '');
    if (!parentOk) { out[r.key] = null; continue; }
    if (r.type === 'bool') { out[r.key] = !!raw; continue; }
    if (r.type === 'select') { out[r.key] = raw == null || raw === '' ? null : raw; continue; }
    if (raw == null || String(raw).trim() === '') { out[r.key] = null; continue; }
    const n = Number(raw);
    if (Number.isNaN(n)) { out[r.key] = null; continue; }
    out[r.key] = r.type === 'money' ? Math.round(n * 100) : Math.round(n);
  }
  return out;
}

/** X132: a row that opens the composer (the free-text one), whatever its words. */
export function isFreeTextRow(row) {
  return !!row?.else;
}

/** A 429 (or any refusal that carries a code) is shown as a notice, with the server's own words. */
export function limitNotice(e) {
  if (!e || typeof e !== 'object') return null;
  const message = Array.isArray(e.global) && e.global.length ? String(e.global[0]) : null;
  if (!message || !e.code) return null;
  return {message, code: String(e.code)};
}

/** Which notices mean "carry on in the forms" rather than "try again". */
export function limitBlocksSession(code) {
  return ['sessionCalls', 'spendCap', 'clubSpend', 'adminSpend'].includes(String(code ?? ''));
}

/**
 * The S11 welcome-back card's words (ScenResume). X145 (Grant 2026-10-26): the server's `sinceYouStarted` now carries only real
 * changes to the club since the session's last turn (plain words, names, no ids); Tap Leagues' own updates to the forms reach the
 * model, never this card. With nothing changed there is no card at all (`show` false): the conversation is simply where it was,
 * and Start again lives in the header (X129).
 */
export function resumeLines(session) {
  const since = session?.sinceYouStarted ?? [];
  // X111 (R47 L8): the card's title already says Welcome back; the line under it does not say it again.
  const intro = since.length ? 'Since you were last here, these things changed in your club:' : 'Your draft is where you left it.';
  return {intro, since, show: since.length > 0};
}

// ── Steps (S1) ──

export function actionWord(action) {
  if (same(action, STEP_ACTION.change)) return 'Change';
  if (same(action, STEP_ACTION.remove)) return 'Remove';
  return 'New';
}

export function actionChipClass(action) {
  if (same(action, STEP_ACTION.remove)) return 'tl-chip--warn';
  if (same(action, STEP_ACTION.change)) return 'tl-chip--brand';
  return 'tl-chip--ok';
}

export function isNewStep(step) { return same(step?.action, STEP_ACTION.new); }
export function isRemoveStep(step) { return same(step?.action, STEP_ACTION.remove); }

/** A step that needs its own tick (Remove, price cut; SE3) and hasn't got it, unless the conflict was settled by keeping the live value. */
export function tickMissing(step) {
  return !!step?.needsTick && !step.ticked && !step.keepLive;
}

/** A conflict (S1) is open until the admin picked keep live or use proposal (ticked). */
export function conflictOpen(step) {
  return !!step?.conflict && !step.keepLive && !step.ticked;
}

export function tickedIds(steps) {
  return (steps ?? []).filter(s => s.ticked).map(s => s.id);
}

/** Why Apply is off, in the order the page says them; empty = can apply. */
export function applyBlockers(check) {
  const out = [];
  if (!check) return ['Checking…'];
  if (check.readOnly) out.push(READ_ONLY_LINE);
  for (const b of check.blockers ?? []) out.push(String(b));
  const steps = check.proposal?.steps ?? [];
  const conflicts = steps.filter(conflictOpen).length;
  if (conflicts) out.push(conflicts === 1 ? 'One step changed since it was drafted: keep the live value or use the proposal.' : `${conflicts} steps changed since they were drafted: keep the live values or use the proposal.`);
  const ticks = steps.filter(tickMissing).length;
  if (ticks) out.push(ticks === 1 ? 'One step needs its own tick before it can be applied.' : `${ticks} steps need their own tick before they can be applied.`);
  if (check.proposal?.hasErrors) out.push('Some steps still have errors. Fix them in the form or ask the assistant.');
  // X74 (R25 L6): the server already says "There is nothing to apply yet." as a blocker; one line, not two.
  if (!steps.length && !out.some(l => /nothing to apply/i.test(l))) out.push('Nothing to apply yet.');
  // X64: the button is never off without a reason beside it. Another admin's proposal is readable, not appliable (SE2);
  // a refusal the server gives no words for still gets a line.
  if (check.proposal?.mine === false) out.push(ANOTHER_ADMINS_LINE);
  if (!check.canApply && !out.length) out.push("This can't be applied right now. Reload the page, or ask the assistant to check the draft again.");
  return out;
}

/**
 * X74 (R25 L7): a first-time setup is every step New AND nothing live yet in the sections the proposal touches (playing
 * areas, memberships & passes, bookings). A one-pass draft on a club that already sells memberships is an addition to a
 * live setup, so "Create setup" / "Check before creating" would read like starting over. With no availability to hand
 * (an older build, not loaded yet) the all-New rule alone decides, as X64 had it.
 */
export function firstSetup(proposal, availability = null) {
  const steps = (proposal?.steps ?? []).filter(s => !s.keepLive);
  const allNew = steps.length ? steps.every(isNewStep) : String(proposal?.applyLabel ?? 'Create setup') === 'Create setup';
  if (!allNew || !availability) return allNew;
  const live = {playingAreas: !!availability.hasPlayingAreas, memberships: !!availability.hasMembershipTypes, bookings: !!availability.bookingsSetUp};
  return !sectionGroups(proposal).some(g => live[g.key]);
}

/** The check page's primary button: "Create setup" for a first-time setup (firstSetup), "Apply now" when anything live changes or the club is already set up (X64, X74). */
export function applyButtonLabel(proposal, availability = null) {
  return firstSetup(proposal, availability) ? 'Create setup' : 'Apply now';
}

/**
 * X119 (R54 L2): where the transcript scrolls once a reply is in: the START of the latest assistant bubble (less a small gap),
 * so a long first bubble's opening sentence and question heading are in view rather than its tail. For a short bubble the
 * browser clamps this to the bottom of the list, as before. No bubble: the bottom.
 */
export function latestTurnScrollTop(list, bubble, gap = 8) {
  if (!list) return 0;
  if (!bubble) return list.scrollHeight ?? 0;
  const top = bubble.getBoundingClientRect().top - list.getBoundingClientRect().top + (list.scrollTop ?? 0) - gap;
  return Math.max(0, Math.round(top));
}

/**
 * X181: whether the reader is at the newest message (within `slack` px of the bottom), as a chat keeps it: a reply or a
 * resize (the composer growing, the keyboard, the window) follows the bottom only while the reader is there; scrolled up to
 * reread, the conversation stays where they put it. No list counts as at the bottom.
 */
export function atConversationBottom(list, slack = 48) {
  if (!list) return true;
  const gap = (list.scrollHeight ?? 0) - (list.scrollTop ?? 0) - (list.clientHeight ?? 0);
  return gap <= slack;
}

/** The draft panel's button and the conversation header's action (X64): "Review and apply" once the assistant says the draft is ready, "Check the draft" before. */
export function draftCheckLabel(proposal) {
  return proposal?.ready ? 'Review and apply' : 'Check the draft';
}

/** The section groups of a proposal in page order, each with its steps. */
export function sectionGroups(proposal) {
  const steps = proposal?.steps ?? [];
  const byId = new Map(steps.map(s => [s.id, s]));
  const listed = new Set();
  const out = [];
  const sections = (proposal?.sections ?? []).length ? proposal.sections : SECTION_ORDER.map(key => ({key, title: SECTION_TITLES[key], stepIds: steps.filter(s => s.section === key).map(s => s.id)}));
  for (const sec of sections) {
    const rows = (sec.stepIds ?? []).map(id => byId.get(id)).filter(Boolean);
    rows.forEach(s => listed.add(s.id));
    if (rows.length) out.push({key: sec.key, title: sec.title || SECTION_TITLES[sec.key] || sec.key, steps: rows});
  }
  const rest = steps.filter(s => !listed.has(s.id));
  if (rest.length) out.push({key: 'other', title: 'Other changes', steps: rest});
  return scoringSectionFirst(out);
}

/**
 * X200: a scoring system step always sits in its own "Scoring systems" group, placed before the competitions (an older API, or
 * a draft saved before X200, filed it under "competitions", so the competitions' "Open to joining by their links" line read as its).
 */
function scoringSectionFirst(groups) {
  const scoring = [];
  const kept = [];
  for (const g of groups) {
    if (g.key === 'scoringSystems') { scoring.push(...g.steps); continue; }
    const own = g.steps.filter(isScoringSystemStep);
    if (own.length) scoring.push(...own);
    const others = own.length ? g.steps.filter(s => !isScoringSystemStep(s)) : g.steps;
    if (others.length) kept.push(own.length ? {...g, steps: others} : g);
  }
  if (!scoring.length) return kept;
  const group = {key: 'scoringSystems', title: SECTION_TITLES.scoringSystems, steps: scoring};
  const at = kept.findIndex(g => !['playingAreas'].includes(g.key));
  if (at < 0) kept.push(group);
  else kept.splice(at, 0, group);
  return kept;
}

/** The draft panel's one-line count per section: "Memberships · 3", "Bookings · 2 sets of booking rules". */
export function sectionCountLine(group) {
  const n = group.steps.length;
  if (group.key === 'bookings') {
    const groups = group.steps.filter(s => s.entityType === ENTITY.areaGroup).length;
    if (groups && groups === n) return `${group.title} · ${groups} set${groups === 1 ? '' : 's'} of booking rules`;
  }
  if (group.key === 'playingAreas' || group.key === 'competitions' || group.key === 'scoringSystems') {
    const fresh = group.steps.filter(isNewStep).length;
    if (fresh === n) return `${group.title} · ${n} new`;
  }
  return `${group.title} · ${n}`;
}

// ── Field words (Live now vs After) ──

const FIELD_WORDS = {
  name: 'Name', description: 'Description', displayAs: 'Show as', requiresApproval: 'Approval', minAge: 'Minimum age', maxAge: 'Maximum age',
  ageRule: 'Age measured', ageOnDay: 'Age date (day)', ageOnMonth: 'Age date (month)', placesLimit: 'Places', nextTypeOnAgeOutId: 'Moves to when they age out',
  length: 'Length', termLength: 'Term length', termUnit: 'Term unit', firstPeriodStartsOn: 'Starts', setDatesEndOn: 'Ends', onSaleDaysBefore: 'On sale days before',
  midPeriodPricing: 'Joining mid-period', autoRenewDefault: 'Renews automatically', priceOptions: 'Prices', refundPolicy: 'Refunds', refundWithinDays: 'Refund within',
  memberCanCancel: 'Members can cancel', questions: 'Questions', termsTemplateGroupId: 'Terms', asksHealthQuestions: 'Health questions', medicalTemplateGroupId: 'Health questionnaire',
  visibility: 'Visibility', status: 'Status',
  sportIds: 'Sports', canBeBooked: 'Can be booked', areaGroupId: 'Group',
  slotMinutes: 'Slot length', cancelCutoffMinutes: 'Cancel up to', playingAreaIds: 'Playing areas', windows: 'Booking hours', whoCanBook: 'Who can book',
  timeZone: 'Time zone', matchBookingOpens: 'Match bookings open', matchBookingDays: 'Match bookings open (days)', allowMatchHolds: 'Hold a playing area while they wait', matchPayer: 'Who pays for a match booking',
  removed: 'Removed',
  // X105: competitions
  sportId: 'Sport', scoringSystemId: 'Scoring system', playersPerTeam: 'Players per team', bonusPoints: 'Bonus points', defaultRolloverChange: 'Up and down between divisions',
  matchLengthMinutes: 'Match length', entryFeeMinor: 'Entry fee', feeMinor: 'Price per session', whoCanEnter: 'Who can enter', memberPrices: 'Member prices',
  cycleName: 'First cycle', cycleStartsOn: 'First cycle starts', cycleEndsOn: 'First cycle ends', joining: 'Joining', linkVisibility: 'Invite link shown to',
  // X132: the club's own scoring system
  template: 'Based on', params: 'Settings changed',
  startDate: 'Starts', endDate: 'Ends', maxRankingDifference: 'Challenge up to (places)', maxRankingDifferenceBelow: 'Challenge down to (places)', challengeExpiryDays: 'Challenge expires after',
  numberUnrestrictedChallenges: 'Free challenges for a newcomer', concurrentChallengeMode: 'Open challenges at once', winMovement: 'After a win', challengeRejectionRule: 'When a challenge is refused',
  challengeRejectionMoveDownPlaces: 'Places dropped', allowMultipleTeamsPerPlayer: 'A player in more than one team', holdJoinsForPlacement: 'New players wait to be placed',
  allowUnscheduledChallengeEntry: 'Results without a scheduled time', maxDifferenceTopPlayers: 'Top players challenge up to (places)', topPlayersCount: 'Top players',
  inactivityDemotionEnabled: 'Drop inactive players', inactivityDemotionDays: 'Inactive after', inactivityDemotionPlaces: 'Places dropped for inactivity',
  format: 'Format', seedingStrategy: 'Seeding', includeThirdPlacePlayoff: 'Third-place match', groupCount: 'Groups', advanceCount: 'Go through per group',
  defaultDivisionSize: 'Players per division', isPrivate: 'Joins need approval', recurrenceMode: 'Repeats', firstOccursAt: 'First date', durationMinutes: 'Session length',
  recurrenceUnit: 'Repeats every', recurrenceInterval: 'Every', monthlyPattern: 'Monthly pattern', recurrenceUntil: 'Until', recurrenceCount: 'Number of sessions',
  minAttendees: 'Minimum players', maxAttendees: 'Places', waitlistEnabled: 'Waiting list', rsvpCloseMinutesBeforeStart: 'RSVPs close before start', rsvpOpenDaysBefore: 'RSVPs open (days before)',
  teamSize: 'Players per side', fixturesPerOccurrence: 'Matches per session', recordFixtures: 'Record the matches',
};

const ENUM_WORDS = {
  Ongoing: 'Ongoing', SetDates: 'Set dates', RepeatingBlocks: 'Repeating blocks',
  OnJoinDate: 'On the day they join', OnFixedDate: 'On a fixed date each year',
  Full: 'Full price', ProRataMonths: 'Pro rata by month', ProRataSessions: 'Pro rata by session',
  None: 'None', FullWithinDays: 'Full refund within a number of days', ProRataUnused: 'Unused time refunded',
  Monthly: 'Monthly instalments', Quarterly: 'Quarterly instalments',
  Public: 'Shown on the club page', Hidden: 'Hidden (link or admin only)',
  Draft: 'Draft', Live: 'Live', ClosedToNew: 'Closed to new members',
  Membership: 'Membership', Pass: 'Pass',
  OnePayer: 'One person pays', Split: 'Split between the sides',
  players: 'Same as the players', days: 'A number of days ahead', deadline: 'Up to the match deadline',
  Week: 'week', Month: 'month', Quarter: 'quarter', Year: 'year',
  // X105: competitions
  Knockout: 'Knockout', GroupsThenKnockout: 'Groups, then a knockout', Random: 'Random draw', GroupFinishingPosition: 'By group finishing position',
  OneAtATime: 'One at a time', OneSentUnlimitedReceived: 'One sent, any number received', Unlimited: 'Any number',
  WinnerGoesAbove: 'Winner goes above the loser', SwapPlaces: 'Winner and loser swap places',
  NoAction: 'Nothing happens', MoveDownPlaces: 'The refuser drops places', ForfeitMatch: 'Counts as a win for the challenger',
  Anyone: 'Anyone', AnyMembership: 'Any membership or pass', Types: 'Chosen memberships or passes',
  Automatic: 'Anyone with the link joins at once', WithApproval: 'Joins wait for an admin',
  ClubPlayers: 'Club players only', Everyone: 'Everyone',
  OneOff: 'One date', AutoRecurring: 'Repeats by a rule', ManualRecurring: 'Dates added by hand',
  DayOfMonth: 'Same day of the month', NthWeekday: 'Same weekday of the month', LastWeekday: 'Last such weekday of the month',
};

export function fieldWord(key) {
  return FIELD_WORDS[key] ?? String(key ?? '').replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase());
}

/** X138 (R68 L2): the words for one changed scoring-schema setting of a club's own system; a key the page does not know reads as plain words ("Hot end 3"). */
const PARAM_WORDS = {
  startScore: v => `Starts at ${v}`,
  doubleIn: v => (v ? 'Double to start' : 'Straight start (no double in)'),
  goldenPoint: v => (v ? 'Golden point at deuce' : 'Advantage at deuce'),
  matchTiebreakTarget: v => `Match tiebreak to ${v}`,
  deuceGame: v => (v ? 'Deuce games' : 'No deuce'),
  legs: v => `${v} legs`,
  frames: v => `${v} frames`,
  periods: v => `${v} periods`,
  raceTo: v => `Race to ${v}`,
  target: v => `First to ${v}`,
  gameTarget: v => `Games to ${v}`,
  winBy: v => `Win by ${v}`,
  cap: v => `Capped at ${v}`,
};
export function paramWord(key, value) {
  const f = PARAM_WORDS[key];
  if (f) return f(value);
  const words = String(key ?? '').replace(/([A-Z])/g, ' $1').toLowerCase().replace(/^./, c => c.toUpperCase());
  return typeof value === 'boolean' ? `${words}: ${value ? 'yes' : 'no'}` : `${words} ${value}`;
}

const isRef = (v) => typeof v === 'string' && v.startsWith('step:');

/** The words for one value: money for prices, lists for hours and rows, names for ids where the page knows them. `ctx` = {currency, names: {id: name}, stepLabels: {stepId: label}}. */
export function valueWord(key, value, ctx = {}) {
  if (value === null || value === undefined || value === '') return 'Not set';
  const money = (minor) => formatMinor(minor ?? 0, ctx.currency || 'gbp');
  const nameOf = (id) => {
    if (isRef(id)) return ctx.stepLabels?.[id.slice(5)] ?? 'New item in this proposal';
    return ctx.names?.[id] ?? String(id);
  };
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  switch (key) {
    case 'entryFeeMinor':
    case 'feeMinor':
      return Number(value) > 0 ? money(Number(value)) : 'Free';
    case 'memberPrices':
      return Array.isArray(value) && value.length
          ? value.map(r => `${nameOf(r.membershipTypeId)}: ${r.eligible ? 'may enter' : 'not eligible'}${r.feeMinor === 0 ? ', free' : r.feeMinor ? `, ${money(r.feeMinor)}` : ''}`).join(' · ')
          : 'None';
    case 'sportId':
    case 'scoringSystemId':
    case 'template':
      return nameOf(value);
    case 'params':
      // X132: the template's settings a club's own scoring system changes. X138 (R68 L2): in words ("Starts at 701", never "startScore 701").
      return value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length
          ? Object.entries(value).map(([k, v]) => paramWord(k, v)).join(' · ')
          : 'None';
    case 'startDate':
    case 'endDate':
    case 'cycleStartsOn':
    case 'cycleEndsOn':
    case 'recurrenceUntil':
      return dateWord(value);
    case 'firstOccursAt':
      return dateTimeWord(value);
    case 'durationMinutes':
    case 'matchLengthMinutes':
      return durationWord(Number(value));
    case 'challengeExpiryDays':
    case 'inactivityDemotionDays':
      return `${value} day${Number(value) === 1 ? '' : 's'}`;
    case 'rsvpCloseMinutesBeforeStart':
      return durationWord(Number(value));
    case 'priceOptions':
      return Array.isArray(value) && value.length
          ? value.map(o => {
            const parts = [money(o.amountMinor)];
            if (o.instalments && !same(o.instalments, 'None')) parts.push(same(o.instalments, 'Monthly') ? 'a month' : 'a quarter');
            else parts.push('all at once');
            if (o.joiningFeeMinor) parts.push(`+ ${money(o.joiningFeeMinor)} joining fee`);
            if (o.label) parts.push(`(${o.label})`);
            return parts.join(' ');
          }).join(' · ')
          : 'Free';
    case 'questions':
      return Array.isArray(value) ? (value.length ? value.map(q => q.label).filter(Boolean).join(' · ') : 'None') : String(value);
    case 'windows':
      return Array.isArray(value) ? (value.length ? value.map(w => `${windowKindWord(w.kind)} ${bandSummary(w)}`).join(' · ') : 'No hours yet (open play)') : String(value);
    case 'whoCanBook':
      return Array.isArray(value) ? (value.length ? value.map(r => whoRowWord(r, ctx)).join(' · ') : 'Not set') : String(value);
    case 'sportIds':
    case 'playingAreaIds':
      return Array.isArray(value) ? (value.length ? value.map(nameOf).join(', ') : (key === 'sportIds' ? 'Any sport' : 'None')) : String(value);
    case 'nextTypeOnAgeOutId':
    case 'areaGroupId':
    case 'termsTemplateGroupId':
    case 'medicalTemplateGroupId':
      return nameOf(value);
    case 'slotMinutes':
      return durationWord(Number(value));
    case 'cancelCutoffMinutes':
      return Number(value) === 0 ? 'Any time' : cutoffWord(Number(value));
    case 'onSaleDaysBefore':
    case 'refundWithinDays':
    case 'matchBookingDays':
      return `${value} day${Number(value) === 1 ? '' : 's'}`;
    case 'termLength':
      return String(value);
    case 'minAge':
    case 'maxAge':
    case 'placesLimit':
      return String(value);
    default:
      if (Array.isArray(value)) return value.length ? value.map(v => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ') : 'None';
      if (typeof value === 'object') return JSON.stringify(value);
      return ENUM_WORDS[String(value)] ?? String(value);
  }
}

/** "6 Jan 2027" from an ISO date or date-time. */
export function dateWord(value) {
  const d = new Date(String(value));
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'});
}

/** "Thu 8 Jan 2027, 19:00" (24-hour clock). */
export function dateTimeWord(value) {
  const d = new Date(String(value));
  if (Number.isNaN(d.getTime())) return String(value);
  return `${d.toLocaleDateString('en-GB', {weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'})}, ${d.toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit', hour12: false})}`;
}

export function windowKindWord(kind) {
  if (same(kind, 'MatchesOnly')) return 'Matches only';
  if (same(kind, 'Closed')) return 'Closed';
  return 'Booking needed';
}

function whoRowWord(r, ctx) {
  const who = same(r.audience, AUDIENCE.noMembership) ? 'No membership'
      : same(r.audience, AUDIENCE.visitor) ? 'Visitors'
          : same(r.audience, AUDIENCE.guest) ? 'Guests'
              : (isRef(r.membershipTypeId) ? (ctx.stepLabels?.[String(r.membershipTypeId).slice(5)] ?? 'New membership') : (ctx.names?.[r.membershipTypeId] ?? 'Membership'));
  if (r.canBook === false) return `${who}: can't book`;
  const parts = [];
  if (r.allDay) parts.push('all day');
  else if (r.maxDurationMinutes != null) parts.push(durationWord(r.maxDurationMinutes));
  if (r.daysAhead != null) parts.push(r.daysAhead === 0 ? 'same day only' : `${r.daysAhead} day${r.daysAhead === 1 ? '' : 's'} ahead`);
  // A visitor holds one booking at a time whatever the row says (BE4), so "N at once" is not theirs (R4 Low 8).
  if (r.maxActiveBookings != null && !same(r.audience, AUDIENCE.visitor)) parts.push(r.maxActiveBookings === 0 ? 'any number at once' : `${r.maxActiveBookings} at once`);
  parts.push(r.priceMinor ? `${formatMinor(r.priceMinor, ctx.currency || 'gbp')} a slot` : 'free');
  return `${who}: ${parts.join(', ')}`;
}

/**
 * X180 (R79 L2): a step's named players as the admin will get them: a name the server matched to someone else's full name, or the
 * one the admin picked on "Which Sam?", reads as that player ("Sam" → "Sam Jones"); a doubles entry keeps its "&". Null when
 * the step has no players.
 */
export function namedPlayersWord(entries, players) {
  if (!Array.isArray(entries) || !entries.length) return null;
  const byName = new Map();
  for (const p of Array.isArray(players) ? players : []) {
    const typed = String(p?.name ?? '').trim().toLowerCase();
    if (typed && !byName.has(typed)) byName.set(typed, p?.matchedName || p?.name);
  }
  const one = (n) => byName.get(String(n ?? '').trim().toLowerCase()) ?? String(n ?? '').trim();
  return entries.map(e => String(e ?? '').split('&').map(one).join(' & ')).join(', ');
}

/** The check page's Live now / After rows: a Change shows only the fields it changes; New and Remove show every field that is set. */
export function stepRows(step, ctx = {}) {
  const rows = stepRowsRaw(step, ctx);
  const named = namedPlayersWord((step?.after ?? step?.fields ?? {}).players, step?.players);
  if (named) for (const r of rows) if (r.key === 'players' && r.after != null) r.after = named;
  return rows;
}

function stepRowsRaw(step, ctx = {}) {
  // X200: a scoring system's details are the server's plain rows (How a match is won, Sets, Tiebreak…), never its structure JSON.
  if (isScoringSystemStep(step)) return scoringRows(step, ctx);
  const after = step?.after ?? step?.fields ?? {};
  const live = step?.liveNow ?? null;
  if (same(step?.action, STEP_ACTION.change) && live) {
    const keys = (step.changedFields?.length ? step.changedFields : Object.keys(step.fields ?? {}));
    return keys.map(k => ({key: k, label: fieldWord(k), before: valueWord(k, live[k], ctx), after: valueWord(k, after[k], ctx)}));
  }
  if (same(step?.action, STEP_ACTION.remove)) {
    return Object.keys(live ?? {}).filter(k => live[k] !== null && live[k] !== undefined && live[k] !== '').map(k => ({key: k, label: fieldWord(k), before: valueWord(k, live[k], ctx), after: null}));
  }
  const rows = Object.keys(after).filter(k => after[k] !== null && after[k] !== undefined && after[k] !== '').map(k => ({key: k, label: fieldWord(k), before: null, after: valueWord(k, after[k], ctx)}));
  // X115 (R50 L1): a new competition whose joining waits for a paid membership never reads "joins at once".
  if (step?.joiningHeldFor) {
    const held = `Held until ${step.joiningHeldFor} is published`;
    const joining = rows.find(r => r.key === 'joining');
    if (joining) joining.after = `${held}; then ${joining.after.charAt(0).toLowerCase()}${joining.after.slice(1)}`;
    else rows.push({key: 'joining', label: fieldWord('joining'), before: null, after: held});
  }
  return rows;
}

/** Fields of a scoring system step that are machinery, never a details row (the structure is JSON; the server's rows say it in words). */
const SCORING_HIDDEN_FIELDS = new Set(['structure']);

/**
 * X200: a scoring system step's details: the API's detailRows (Sport, Name, How a match is won, Sets, Tiebreak, What gets
 * entered, Table points; for a copy, what it starts from and the settings it changes). An older API without them: the step's
 * fields in words, the structure left out (its words are the summary).
 */
export function scoringRows(step, ctx = {}) {
  if (Array.isArray(step?.detailRows) && step.detailRows.length) {
    return step.detailRows.map((r, i) => ({key: `d${i}-${r.label}`, label: String(r.label ?? ''), before: null, after: String(r.value ?? '')}));
  }
  const after = step?.after ?? step?.fields ?? {};
  return Object.keys(after)
      .filter(k => !SCORING_HIDDEN_FIELDS.has(k) && after[k] !== null && after[k] !== undefined && after[k] !== '' && (typeof after[k] !== 'object' || k === 'params'))
      .map(k => ({key: k, label: fieldWord(k), before: null, after: valueWord(k, after[k], ctx)}));
}

/** X200: a drafted scoring system the check page can preview (the API says so; an older API: a New scoring step). */
export function canPreviewScoring(step) {
  if (!isScoringSystemStep(step) || !isNewStep(step)) return false;
  return step.canPreviewScoring !== undefined ? !!step.canPreviewScoring : false;
}

/** "Live now" for a Remove step, in words. */
export function removeAfterWord(step) {
  return step?.entityType === ENTITY.membershipType ? 'Closed to new members. Current holders keep it until it ends.' : 'Removed';
}

// ── Edit in form ──

export const ALLOWED_FIELDS = Object.freeze({
  [ENTITY.membershipType]: ['name', 'description', 'displayAs', 'requiresApproval', 'minAge', 'maxAge', 'ageRule', 'ageOnDay', 'ageOnMonth', 'placesLimit', 'nextTypeOnAgeOutId',
    'length', 'termLength', 'termUnit', 'firstPeriodStartsOn', 'setDatesEndOn', 'onSaleDaysBefore', 'midPeriodPricing', 'autoRenewDefault', 'priceOptions', 'refundPolicy', 'refundWithinDays',
    'memberCanCancel', 'questions', 'termsTemplateGroupId', 'asksHealthQuestions', 'medicalTemplateGroupId', 'visibility'],
  [ENTITY.playingArea]: ['name', 'sportIds', 'canBeBooked', 'areaGroupId'],
  [ENTITY.areaGroup]: ['name', 'slotMinutes', 'cancelCutoffMinutes', 'playingAreaIds', 'windows', 'whoCanBook'],
  [ENTITY.bookingSettings]: ['timeZone', 'matchBookingOpens', 'matchBookingDays', 'allowMatchHolds', 'matchPayer'],
  // X115 (R50 H1): "Edit in form" for a new competition step (the API's allowlist, SetupDraftAllowlist).
  [ENTITY.league]: ['name', 'sportId', 'scoringSystemId', 'playersPerTeam', 'bonusPoints', 'defaultRolloverChange', 'matchLengthMinutes', 'entryFeeMinor', 'whoCanEnter', 'memberPrices', 'cycleName', 'cycleStartsOn', 'cycleEndsOn', 'joining', 'linkVisibility'],
  [ENTITY.ladder]: ['name', 'sportId', 'scoringSystemId', 'playersPerTeam', 'startDate', 'endDate', 'maxRankingDifference', 'maxRankingDifferenceBelow', 'challengeExpiryDays', 'numberUnrestrictedChallenges', 'concurrentChallengeMode', 'winMovement', 'challengeRejectionRule', 'challengeRejectionMoveDownPlaces', 'allowMultipleTeamsPerPlayer', 'holdJoinsForPlacement', 'allowUnscheduledChallengeEntry', 'maxDifferenceTopPlayers', 'topPlayersCount', 'inactivityDemotionEnabled', 'inactivityDemotionDays', 'inactivityDemotionPlaces', 'matchLengthMinutes', 'entryFeeMinor', 'whoCanEnter', 'memberPrices', 'joining', 'linkVisibility'],
  [ENTITY.tournament]: ['name', 'sportId', 'scoringSystemId', 'playersPerTeam', 'format', 'startDate', 'endDate', 'seedingStrategy', 'includeThirdPlacePlayoff', 'bonusPoints', 'groupCount', 'advanceCount', 'matchLengthMinutes', 'entryFeeMinor', 'whoCanEnter', 'memberPrices', 'joining', 'linkVisibility'],
  [ENTITY.americano]: ['name', 'sportId', 'scoringSystemId', 'defaultDivisionSize', 'defaultRolloverChange', 'matchLengthMinutes', 'entryFeeMinor', 'whoCanEnter', 'memberPrices', 'cycleName', 'cycleStartsOn', 'cycleEndsOn', 'joining', 'linkVisibility'],
  [ENTITY.meet]: ['name', 'description', 'sportId', 'scoringSystemId', 'isPrivate', 'recurrenceMode', 'firstOccursAt', 'durationMinutes', 'recurrenceUnit', 'recurrenceInterval', 'monthlyPattern', 'recurrenceUntil', 'recurrenceCount', 'minAttendees', 'maxAttendees', 'waitlistEnabled', 'rsvpCloseMinutesBeforeStart', 'rsvpOpenDaysBefore', 'teamSize', 'fixturesPerOccurrence', 'recordFixtures', 'feeMinor', 'whoCanEnter', 'memberPrices', 'linkVisibility'],
});

/**
 * X115 (R50 H1): the competition edit dialog's fields: the step's own fields with the form's edits laid over, nothing the
 * allowlist refuses, and a fee of 0 or an empty box as free (null), exactly as the API stores it.
 */
export function competitionFieldsFromForm(step, edits) {
  const entity = step?.entityType;
  const allowed = new Set(ALLOWED_FIELDS[entity] ?? []);
  const out = {};
  for (const [k, v] of Object.entries(step?.fields ?? {})) if (allowed.has(k)) out[k] = v;
  for (const [k, v] of Object.entries(edits ?? {})) if (allowed.has(k)) out[k] = v === '' ? null : v;
  const feeKey = entity === ENTITY.meet ? 'feeMinor' : 'entryFeeMinor';
  if (feeKey in out && (out[feeKey] === 0 || out[feeKey] === '0' || out[feeKey] == null)) out[feeKey] = null;
  return out;
}

const NESTED_FIELDS = Object.freeze({
  priceOptions: ['amountMinor', 'instalments', 'joiningFeeMinor', 'label'],
  questions: ['fieldType', 'label', 'help', 'required', 'options', 'longAnswer'],
  windows: ['days', 'startLocal', 'endLocal', 'kind'],
  whoCanBook: ['audience', 'membershipTypeId', 'canBook', 'maxDurationMinutes', 'allDay', 'daysAhead', 'maxActiveBookings', 'priceMinor'],
});

/** Only what the API's allowlist (SE1) accepts from a form, nested rows included; ids, club, sort orders and versions never go. */
export function stepFieldsFromForm(entityType, body) {
  const allowed = ALLOWED_FIELDS[entityType] ?? [];
  const out = {};
  for (const key of allowed) {
    if (!(key in (body ?? {}))) continue;
    const value = body[key];
    if (NESTED_FIELDS[key] && Array.isArray(value)) {
      out[key] = value.map(row => {
        const picked = {};
        for (const nk of NESTED_FIELDS[key]) if (nk in (row ?? {})) picked[nk] = row[nk];
        return picked;
      });
    } else {
      out[key] = value === '' ? null : value;
    }
  }
  return out;
}

/** The membership type editor is the manual form for a type step: a route with the proposal and step (the editor pre-fills and saves back to the step). Other entities edit in a dialog on the check page. */
export function stepEditRoute(step, clubId, proposalId) {
  if (step?.entityType !== ENTITY.membershipType) return null;
  return {name: 'ClubMembershipType', params: {clubId, typeId: step.targetId ?? 'new'}, query: {proposal: proposalId, step: step.id}};
}

/**
 * X105: a competition step has no form to open before it exists; a live one opens its own admin page (the API's
 * formPath, e.g. /admin/clubs/{id}/leagues/{id}). The step card shows "Open its page" for a Change and nothing for a New.
 */
export function competitionPagePath(step) {
  if (!isCompetitionStep(step) || !step?.targetId) return null;
  return step.formPath || null;
}

/** The editor's form from a step's "after" record: allowlisted fields over the blank form. */
export function typeFormFromStep(step, blank) {
  const after = step?.after ?? step?.fields ?? {};
  const form = {...blank};
  for (const key of ALLOWED_FIELDS[ENTITY.membershipType]) {
    if (!(key in after) || key === 'visibility') continue;
    form[key] = after[key];
  }
  form.priceOptions = (after.priceOptions ?? []).map((o, i) => ({id: null, amountMinor: o.amountMinor ?? 0, instalments: o.instalments ?? 'None', joiningFeeMinor: o.joiningFeeMinor ?? null, label: o.label ?? null, sortOrder: i}));
  form.questions = (after.questions ?? []).map((q, i) => ({id: null, fieldType: q.fieldType, label: q.label, help: q.help ?? null, required: !!q.required, optionsJson: q.optionsJson ?? (Array.isArray(q.options) ? JSON.stringify(q.options) : null), longAnswer: !!q.longAnswer, sortOrder: i}));
  if (typeof form.firstPeriodStartsOn === 'string') form.firstPeriodStartsOn = form.firstPeriodStartsOn.slice(0, 10);
  if (typeof form.setDatesEndOn === 'string') form.setDatesEndOn = form.setDatesEndOn.slice(0, 10);
  if (isRef(form.nextTypeOnAgeOutId)) form.nextTypeOnAgeOutId = null;
  return form;
}

// ── Payments (S9) ──

/**
 * The "Before you can charge" action for a payment state: open the embedded onboarding, go to Payments, or nothing.
 * X147 (Grant: "There needs to be a button taking me to the stripe setup dialog surely?"): the server names the button
 * (payments.action = {kind: 'paymentsSetup', label}: "Set up payments" / "Finish with Stripe" / "Reconnect", absent when there is
 * nothing to press or the viewer can't set up payments); an older API's nextStep gives the same labels.
 */
export function paymentAction(payments) {
  if (payments?.action?.kind === 'paymentsSetup' && payments.action.label) return {label: String(payments.action.label), kind: 'onboard'};
  switch (String(payments?.nextStep ?? '')) {
    case 'setUp': return {label: 'Set up payments', kind: 'onboard'};
    case 'reconnect': return {label: 'Reconnect', kind: 'onboard'};
    case 'finishWithStripe': return {label: 'Finish with Stripe', kind: 'onboard'};
    case 'restricted': return {label: 'Open Payments', kind: 'payments'};
    default: return null;
  }
}

/**
 * X147: the Stripe button under an assistant bubble. The server marks the turns whose words are about the payment account
 * (turn.actions has a paymentsSetup action); the label follows the account as it is NOW (session.payments after the dialog
 * closes: set up since → no button; started since → "Finish with Stripe"), and a viewer who can't set up payments (impersonating,
 * read-only) gets the words alone. Null = no button.
 */
export function turnPaymentsButton(turn, payments, readOnly = false) {
  if (readOnly) return null;
  const marked = Array.isArray(turn?.actions) && turn.actions.some(a => a?.kind === 'paymentsSetup');
  if (!marked) return null;
  const now = paymentAction(payments);
  if (now?.kind === 'onboard') return {label: now.label};
  // An older API without payments.action still sent the turn's own label; nothing current to press means no button.
  if (!payments || payments.nextStep == null) {
    const own = turn.actions.find(a => a?.kind === 'paymentsSetup' && a.label);
    return own ? {label: String(own.label)} : null;
  }
  return null;
}

/**
 * X167 (Grant: "it shouldn't ask me to do two things at once right?"): the payments-first bubble carries the Stripe button and a
 * quiet second action (kind paymentsLater) that moves the conversation on. Only on the last turn (still open); it reads "Not now"
 * while there is a payments button to press and "Continue" once the account was set up in place. Null = no quiet action.
 */
export const PAYMENTS_LATER_HANDLE = 'payments-later';
export function turnLaterAction(turn, isLast, hasPaymentsButton) {
  if (!isLast || !Array.isArray(turn?.actions) || !turn.actions.some(a => a?.kind === 'paymentsLater')) return null;
  return {label: hasPaymentsButton ? 'Not now' : 'Continue', handle: PAYMENTS_LATER_HANDLE};
}

/** The short state word beside the payment line (ScenPayStates). */
export function paymentStateWord(payments) {
  switch (String(payments?.nextStep ?? '')) {
    case 'off': return 'Payments not available';
    case 'setUp': return 'No payment account';
    case 'reconnect': return 'Disconnected';
    case 'finishWithStripe': return 'Started, not finished';
    case 'wait': return 'Being checked';
    case 'ready': return 'Ready';
    case 'restricted': return 'Needs attention';
    default: return '';
  }
}

// ── Saved proposals (S1) and done (S8) ──

/** "2026-10-19": the calendar date of an instant in a zone (the club's, else this machine's). */
const dateKey = (ms, timeZone) => {
  try { return new Date(ms).toLocaleDateString('en-CA', timeZone ? {timeZone} : undefined); } catch { return new Date(ms).toLocaleDateString('en-CA'); }
};
const dayNumber = (key) => { const [y, m, d] = String(key).split('-').map(Number); return Math.round(Date.UTC(y, m - 1, d) / 86400000); };

/**
 * "today" / "yesterday" / "3 days ago" / "12 Oct". X74 (R25 L5): by calendar dates in the club's zone, so something
 * applied at 7:36pm yesterday reads "yesterday" at 8am today (elapsed 24-hour periods said "today").
 */
export function whenWord(iso, now = Date.now(), timeZone = null) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const days = dayNumber(dateKey(now, timeZone)) - dayNumber(dateKey(t, timeZone));
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  let opts = {day: 'numeric', month: 'short'};
  if (timeZone) { try { new Date(t).toLocaleDateString('en-GB', {...opts, timeZone}); opts = {...opts, timeZone}; } catch { /* an unknown zone: this machine's */ } }
  return new Date(iso).toLocaleDateString('en-GB', opts);
}

export function freshnessWord(item) {
  if (same(item?.status, PROPOSAL_STATUS.applied)) return item.canUndo ? 'Applied · can be undone' : 'Applied';
  if (same(item?.status, PROPOSAL_STATUS.undone)) return 'Undone';
  if (same(item?.status, PROPOSAL_STATUS.discarded)) return 'Discarded';
  if (same(item?.status, PROPOSAL_STATUS.draft)) return 'Conversation not finished';
  if (same(item?.freshness, 'outOfDate')) return 'Out of date';
  return 'Up to date';
}

export function freshnessChipClass(item) {
  const w = freshnessWord(item);
  if (w === 'Up to date') return 'tl-chip--ok';
  if (w === 'Out of date') return 'tl-chip--warn';
  if (w.startsWith('Applied')) return 'tl-chip--brand';
  return '';
}

export function proposalTitle(item) {
  // X101 (R40 L1): an applied one counts what applying did (the API's appliedStepCount, keep-the-live-value steps left out),
  // the same count as the done page's "Applied …: N changes".
  const n = item?.appliedStepCount ?? item?.stepCount ?? 0;
  const focus = same(item?.focus, FOCUS.memberships) ? 'Memberships & passes' : same(item?.focus, FOCUS.bookings) ? 'Bookings' : same(item?.focus, FOCUS.competitions) ? 'Competitions' : 'Club setup';
  return `${focus} · ${n} change${n === 1 ? '' : 's'}`;
}

/** "By Grant, today · applied yesterday" (days in the club's zone when given, X74) */
export function proposalLine(item, now = Date.now(), timeZone = null) {
  const who = item?.mine ? 'you' : (item?.adminName || 'another admin');
  const parts = [`By ${who}, ${whenWord(item?.updatedAt ?? item?.createdAt, now, timeZone)}`];
  if (item?.appliedAt) parts.push(`applied ${whenWord(item.appliedAt, now, timeZone)}`);
  else if (item?.discardsAt) parts.push(`discarded on ${new Date(item.discardsAt).toLocaleDateString('en-GB', {day: 'numeric', month: 'short'})} if not applied`);
  return parts.join(' · ');
}

/** When the undo window closes: the API's undoDeadline, else appliedAt + 7 days (the list carries undoDeadline since X64; older builds only appliedAt). */
export function undoDeadlineOf(item) {
  if (item?.undoDeadline) return new Date(item.undoDeadline);
  if (!item?.appliedAt) return null;
  const t = new Date(item.appliedAt).getTime();
  if (Number.isNaN(t)) return null;
  return new Date(t + UNDO_DAYS * 86400000);
}

/** "Mon 26 Oct" */
export function dayWord(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', {weekday: 'short', day: 'numeric', month: 'short'});
}

/**
 * The Memberships page's two lists (X64, Grant 2026-10-19: an applied proposal sat under "Proposed changes … nothing here is
 * live until it's applied"): open ones (draft / ready) under "Proposed changes"; applied ones still inside the undo window under
 * "Recently applied"; undone, discarded and applied-past-the-window never show.
 */
export function splitProposals(items, now = Date.now()) {
  const open = [];
  const applied = [];
  for (const p of items ?? []) {
    const status = String(p?.status ?? '').toLowerCase();
    if (status === 'draft' || status === 'ready') open.push(p);
    else if (status === 'applied') {
      const deadline = undoDeadlineOf(p);
      if (deadline && deadline.getTime() > now) applied.push(p);
    }
  }
  return {open, applied};
}

/** "applied today by you · Undo until Mon 26 Oct" (the Recently applied row, after its title; days in the club's zone, X74). */
export function appliedLine(item, now = Date.now(), timeZone = null) {
  const who = item?.mine ? 'you' : (item?.adminName || 'another admin');
  const parts = [`applied ${whenWord(item?.appliedAt, now, timeZone)} by ${who}`];
  const deadline = undoDeadlineOf(item);
  if (deadline && deadline.getTime() > now) parts.push(`Undo until ${dayWord(deadline)}`);
  return parts.join(' · ');
}

/** The row's main action: Continue (my unfinished conversation), Review and apply (mine, ready), Review (somebody else's), Undo (applied). */
export function proposalAction(item) {
  if (same(item?.status, PROPOSAL_STATUS.applied)) return item.canUndo ? {label: 'Undo', kind: 'undo'} : {label: 'View', kind: 'review'};
  if (same(item?.status, PROPOSAL_STATUS.undone) || same(item?.status, PROPOSAL_STATUS.discarded)) return {label: 'View', kind: 'review'};
  if (item?.mine && same(item?.status, PROPOSAL_STATUS.draft)) return {label: 'Continue', kind: 'continue'};
  if (item?.mine) return {label: 'Review and apply', kind: 'review'};
  return {label: 'Review', kind: 'review'};
}

/** A next step from the apply response, with the button the page can attach to it (the API sends plain sentences). */
export function nextStepAction(text) {
  const t = String(text ?? '').toLowerCase();
  if (t.includes('payment account') || t.includes('stripe')) return {label: 'Set up now', kind: 'payments'};
  if (t.includes('publish')) return {label: 'Open memberships', kind: 'memberships'};
  if (t.includes('bookings on') || t.includes('switch bookings')) return {label: 'Switch on', kind: 'bookings'};
  if (t.includes('share') && t.includes('link')) return {label: 'Links above', kind: 'links'};
  if (t.includes('invite')) return {label: 'Share', kind: 'invite'};
  if (t.includes('competition')) return {label: 'Create', kind: 'competitions'};
  if (t.includes('health') || t.includes('terms')) return {label: 'Review', kind: 'memberships'};
  return null;
}

/** "Available until 24 Oct" / "No longer available". */
export function undoDeadlineWord(deadlineIso, now = Date.now()) {
  if (!deadlineIso) return '';
  const t = new Date(deadlineIso).getTime();
  if (Number.isNaN(t) || t <= now) return 'No longer available';
  return `Available until ${new Date(deadlineIso).toLocaleDateString('en-GB', {day: 'numeric', month: 'short'})}`;
}

/** "4 memberships created, unpublished" lines for the done page from what was created. X115 (R50 L1, L2): a type published at apply is never "unpublished"; a competition whose joining waits is never "open to joining". */
export function createdLines(created = {}, proposal = null, links = []) {
  const out = [];
  const nameOf = (entity, ids) => (proposal?.steps ?? []).filter(s => s.entityType === entity && isNewStep(s)).map(s => s.label).filter(Boolean).slice(0, ids.length);
  const types = created[ENTITY.membershipType] ?? [];
  if (types.length) {
    const typeSteps = (proposal?.steps ?? []).filter(s => s.entityType === ENTITY.membershipType && isNewStep(s));
    const published = typeSteps.filter(s => s.publishedAtApply).map(s => s.label).filter(Boolean);
    const noun = `membership${types.length === 1 ? '' : 's'} or pass${types.length === 1 ? '' : 'es'}`;
    const title = !published.length ? `${types.length} ${noun} created, unpublished`
        : published.length >= types.length ? `${types.length} ${noun} created and published (free)`
            : `${types.length} ${noun} created: ${published.length} published (free), ${types.length - published.length} unpublished`;
    out.push({title, sub: nameOf(ENTITY.membershipType, types).join(', ')});
  }
  const areas = created[ENTITY.playingArea] ?? [];
  if (areas.length) out.push({title: `${areas.length} playing area${areas.length === 1 ? '' : 's'} added`, sub: nameOf(ENTITY.playingArea, areas).join(', ')});
  const groups = created[ENTITY.areaGroup] ?? [];
  if (groups.length) out.push({title: groups.length === 1 ? 'Booking rules ready for bookings' : `${groups.length} sets of booking rules ready for bookings`, sub: 'Bookings are still off for players'});
  // X138 (R68 L2): the club's own scoring system made for a competition of this proposal is listed too (it is on the Scoring systems page).
  const systems = created[ENTITY.scoringSystem] ?? [];
  if (systems.length) out.push({title: `${systems.length} scoring system${systems.length === 1 ? '' : 's'} created, the club's own`, sub: nameOf(ENTITY.scoringSystem, systems).join(', ')});
  // X105 (C3): competitions, each open to joining by its link (the rows above carry the links), unless its joining waits for a paid membership (X111 M6, X115 L1).
  const heldFor = (entity) => {
    const fromLinks = (links ?? []).filter(l => l.heldFor && (l.entityType === entity || l.format === competitionFormatOf(entity)));
    if (fromLinks.length) return fromLinks.map(l => l.heldFor);
    return (proposal?.steps ?? []).filter(s => s.entityType === entity && isNewStep(s) && s.joiningHeldFor).map(s => s.joiningHeldFor);
  };
  for (const entity of COMPETITION_ENTITIES) {
    const ids = created[entity] ?? [];
    if (!ids.length) continue;
    const word = competitionWord(entity);
    const held = heldFor(entity);
    const title = held.length >= ids.length ? `${ids.length} ${ids.length === 1 ? word : word + 's'} created; joining opens once ${listWords([...new Set(held)])} is published`
        : held.length ? `${ids.length} ${word}s created: ${ids.length - held.length} open to joining by ${ids.length - held.length === 1 ? 'its' : 'their'} link, ${held.length} waiting for ${listWords([...new Set(held)])}`
            : `${ids.length} ${ids.length === 1 ? word : word + 's'} created, open to joining by ${ids.length === 1 ? 'its' : 'their'} link`;
    out.push({title, sub: nameOf(entity, ids).join(', ')});
  }
  const changedSteps = (proposal?.steps ?? []).filter(s => !isNewStep(s) && !s.keepLive);
  const changed = changedSteps.length;
  // R6 Low 3: the renewal note is about memberships; a bookings change has its own. X111 (R47 L6): a competition change
  // (a fee, who can enter) gets neither; it is just live.
  const touchesTypes = changedSteps.some(s => s.entityType === ENTITY.membershipType);
  const touchesBookings = changedSteps.some(s => [ENTITY.areaGroup, ENTITY.bookingSettings, ENTITY.playingArea].includes(s.entityType));
  const sub = touchesTypes ? 'Current members keep what they have until their next renewal'
      : touchesBookings ? 'Live now. Any bookings it changed were listed on the check page, and their players were told'
          : 'Live now';
  if (changed) out.push({title: `${changed} change${changed === 1 ? '' : 's'} to what was already live`, sub});
  return out;
}

/**
 * X105 (C3): the done page leads with each new competition's link. Rows from the API's competition links (the apply
 * response or, on a reload, the proposal's), with the format key the club home's icons use and the invite path.
 */
export function competitionLinkRows(links) {
  return (links ?? []).map((l) => ({
    stepId: l.stepId,
    entityType: l.entityType,
    format: l.format || competitionFormatOf(l.entityType),
    word: competitionWord(l.entityType),
    label: l.label,
    id: l.id,
    joinReferenceType: l.joinReferenceType,
    joinReferenceId: l.joinReferenceId,
    url: l.joinUrl || '',
    slug: l.joinSlug || '',
    invitePath: l.joinSlug ? '/invite/' + l.joinSlug : null,
    pagePath: l.pagePath || null,
    // X111 (R47 M6): joining waits for these paid memberships (drafted alongside) to be published; no link exists yet.
    heldFor: l.joiningHeldFor || null,
    // X167 (C3): the players the setup put in by name, said on the row
    players: linkPlayersLine(l),
  }));
}

/**
 * X167 (C3): "3 players put in (1 new to the club, no account yet). They wait in the waiting area to be seated from its page."
 * from the link row's playersAdded / playersNew / playersWaiting; null when the setup named nobody for it.
 */
export function linkPlayersLine(l) {
  const added = Number(l?.playersAdded ?? 0);
  if (!added) return null;
  const fresh = Number(l?.playersNew ?? 0);
  const head = `${added} player${added === 1 ? '' : 's'} put in${fresh ? ` (${fresh} new to the club, no account yet)` : ''}.`;
  return l?.playersWaiting ? `${head} They wait in the waiting area to be seated from its page.` : head;
}

/** X111 (R47 M1): the undone hero's line: everything gone, or most of it with what stayed listed below. X115 (R50 L9): a changes-only undo never says something created is gone. */
export function undoneLine(undoResult, proposal = null) {
  const kept = (undoResult?.kept ?? []).length;
  const steps = (proposal?.steps ?? []).filter(s => !s.keepLive);
  const created = steps.some(isNewStep);
  const changed = steps.some(s => !isNewStep(s));
  const tail = kept ? `; ${kept === 1 ? '1 item stayed' : `${kept} items stayed`}, listed below.` : '.';
  if (steps.length && changed && !created) return `The changes are undone and the settings are back as they were${tail}`;
  if (steps.length && created && !changed) return kept ? `Most of what it created is gone${tail}` : 'What it created is gone.';
  if (!kept) return 'What it created is gone and changed settings are back as they were.';
  return `Most of what it created is gone and changed settings are back as they were${tail}`;
}

/** X111 (R47 M6): the done page's line under "Your setup is created": competitions are open by their links at once; memberships and bookings wait for you. */
export function appliedWords(proposal, links = []) {
  const steps = proposal?.steps ?? [];
  if (steps.some(st => !isNewStep(st) && !st.keepLive)) return 'The changes are live now.';
  const open = (links ?? []).filter(l => !l.heldFor).length;
  const held = (links ?? []).filter(l => l.heldFor);
  const parts = [];
  if (open) parts.push(open === 1 ? 'The new competition is open to joining by its link' : 'The new competitions are open to joining by their links');
  if (held.length) parts.push(`${held.map(l => l.label).join(' and ')} ${held.length === 1 ? 'opens' : 'open'} to joining once ${held.map(l => l.heldFor).join(' and ')} is published`);
  // X115 (R50 L2): a free membership published at apply is visible already; only unpublished types and bookings wait for the admin.
  const typeSteps = steps.filter(st => st.entityType === ENTITY.membershipType);
  const published = typeSteps.filter(st => st.publishedAtApply).map(st => st.label).filter(Boolean);
  const unpublished = typeSteps.length - published.length;
  const bookings = steps.some(st => [ENTITY.areaGroup, ENTITY.bookingSettings].includes(st.entityType));
  if (published.length) parts.push(`${listWords(published)} ${published.length === 1 ? 'is' : 'are'} free and published`);
  const rest = unpublished > 0 && bookings ? 'nothing else is visible to players until you publish a membership or switch bookings on'
      : unpublished > 0 ? 'nothing else is visible to players until you publish a membership'
          : bookings ? 'bookings stay off for players until you switch them on'
              : null;
  if (!parts.length) {
    if (unpublished > 0 && bookings) return 'Nothing is visible to players until you publish a membership or switch bookings on.';
    if (unpublished > 0) return 'Nothing is visible to players until you publish a membership.';
    if (bookings) return 'Bookings stay off for players until you switch them on.';
    return 'Nothing is visible to players yet.';
  }
  const head = parts.join('; ');
  return rest ? `${head}; ${rest}.` : `${head}.`;
}

/** X101 (R40 L1): every change applying made (new and changed; keep-the-live-value steps are skipped): the done page's
 *  "Applied …: 6 changes", the same number as the Recently applied line's proposalTitle. */
export function appliedChangeCount(proposal) {
  return (proposal?.steps ?? []).filter(s => !s.keepLive).length;
}

/** "6 changes" / "1 change" */
export function changesWord(n) {
  return `${n} change${n === 1 ? '' : 's'}`;
}

// ── X178 (Grant 2026-10-27): the floating, expandable draft on the conversation page ──

/** Anything for the draft to show: a step, an assumption, a problem, a "not possible yet" or a "before you can charge" line. */
export function draftHasContent(proposal) {
  if (!proposal) return false;
  return ['steps', 'assumptions', 'unsupported', 'errors', 'beforeYouCanCharge'].some(k => (proposal[k] ?? []).length > 0);
}

/** One step as the draft lists it: "Adult membership" for a new one, "Change · Adult membership" otherwise. */
export function draftStepLine(step) {
  if (!step) return '';
  const label = String(step.label ?? '').trim();
  return isNewStep(step) ? label : `${actionWord(step.action)} · ${label}`;
}

/** What a step reads as, to tell whether a turn changed it. */
// X182 (R80 L3): the WHOLE step, keys sorted (a stable hash): a turn that only set a detail (the ladder's scoring, a date) changes
// the draft too, so the bar pulses; the label/summary alone missed it.
const stableJson = (v) => {
  if (Array.isArray(v)) return `[${v.map(stableJson).join(',')}]`;
  if (v && typeof v === 'object') return `{${Object.keys(v).sort().filter(k => v[k] !== undefined).map(k => `${JSON.stringify(k)}:${stableJson(v[k])}`).join(',')}}`;
  return JSON.stringify(v ?? null);
};
const stepKey = (s) => stableJson(s ?? null);

/** A fingerprint of what the draft shows, so the page can tell when a turn changed it (and pulse the collapsed draft). */
export function draftSignature(proposal) {
  if (!proposal) return '';
  return JSON.stringify([
    (proposal.steps ?? []).map(s => [s.id, stepKey(s)]),
    (proposal.assumptions ?? []).map(a => a?.text ?? a),
    (proposal.unsupported ?? []).map(u => u?.request ?? u),
    proposal.errors ?? [],
    proposal.ready ?? false,
  ]) + stableJson(proposal.sections ?? []);
}

/**
 * The step to name on the collapsed draft: the first one this turn added or changed (against the draft before it); with nothing
 * to compare, or nothing new, the last step. Null when the draft has no steps.
 */
export function latestDraftStep(previous, next) {
  const steps = next?.steps ?? [];
  if (!steps.length) return null;
  if (previous) {
    const before = new Map((previous.steps ?? []).map(s => [s.id, stepKey(s)]));
    const changed = steps.find(s => before.get(s.id) !== stepKey(s));
    if (changed) return changed;
  }
  return steps[steps.length - 1];
}

/** The collapsed draft's heading: "Draft · 4 changes", "Applied · 4 changes", "Undone · 4 changes"; with no steps "Your draft"; with no draft at all, the club notes. */
export function draftDockTitle(proposal, {applied = false, undone = false, notesCount = 0} = {}) {
  const n = proposal?.steps?.length ?? 0;
  const word = undone ? 'Undone' : applied ? 'Applied' : 'Draft';
  if (n) return `${word} · ${changesWord(n)}`;
  if (draftHasContent(proposal)) return 'Your draft';
  return notesCount ? `${CLUB_NOTES_TITLE} · ${notesCount}` : CLUB_NOTES_TITLE;
}

/** The collapsed draft's second line: the latest change, else what the draft is waiting on, else nothing. */
export function draftDockLine(step, proposal, question = null) {
  if (step) return draftStepLine(step);
  if (question?.text) return `Waiting on you: ${question.text}`;
  if ((proposal?.errors ?? []).length) return `Needs fixing · ${proposal.errors.length}`;
  if ((proposal?.assumptions ?? []).length) return `Things I assumed · ${proposal.assumptions.length}`;
  if ((proposal?.beforeYouCanCharge ?? []).length) return 'Before you can charge';
  return '';
}

/**
 * X182 (R80 M2): the composer's height: the words' own height up to `maxRows` lines (Grant: max 6), then it stays there and
 * scrolls inside itself. Empty = one line, whatever the placeholder.
 * @returns {{height:number, scrolls:boolean}}
 */
export function composerHeight({scrollHeight, lineHeight, padding = 0, maxRows = 6, empty = false}) {
  const one = lineHeight + padding;
  const max = lineHeight * maxRows + padding;
  if (empty) return {height: one, scrolls: false};
  const h = Math.max(one, Math.min(scrollHeight, max));
  return {height: h, scrolls: scrollHeight > max + 1};
}

// X224 (R93-M1): a club's setup assistant page (the conversation and its proposal / done pages), where the assistant is
// already open: the "Meet the assistant" coach mark never shows there, and arriving counts as having met it.
export const isSetupAssistantPath = (path) => typeof path === 'string' && /\/clubs\/[^/]+\/setup-assistant(\/|$)/.test(path);

// ── The way out of the done page (Grant 2026-11-02: "The main action on this screen should be Done ... return to the
// club page, or wherever they started the assistant from"; "there shouldn't even be a way back after that, it's been
// applied") ──

/**
 * A place Done may go back to: one of the site's own admin paths (as a route's fullPath: path + query), never an
 * absolute or protocol-relative address and never one of the assistant's own pages. Null otherwise.
 */
export function safeReturnPath(raw) {
  if (typeof raw !== 'string' || !/^\/admin\/[^\s\\]*$/.test(raw)) return null;
  if (isSetupAssistantPath(raw.split(/[?#]/)[0])) return null;
  return raw;
}

/** Where the done page's Done lands: the ?returnTo it was opened with when that is safe, else the club admin home. */
export function doneTarget(returnTo, clubId) {
  return safeReturnPath(returnTo) ?? {name: 'ClubDashboard', params: {clubId}};
}

/**
 * The query every way into the assistant (and each assistant route on to the next) adds: the page the admin is on, when
 * it qualifies; on the assistant's own pages the returnTo they were opened with is carried on instead. Nothing otherwise.
 */
export function assistantReturnQuery(route) {
  const path = safeReturnPath(route?.fullPath) ?? safeReturnPath(route?.query?.returnTo);
  return path ? {returnTo: path} : {};
}

/**
 * The conversation page's record of where it sits in the browser's history (vue-router's history.state: position and the
 * path before it), taken only when it was arrived at from outside the assistant (never on a Back-to-the-conversation
 * push from the check page). Null when there is nothing to record.
 */
export function setupEntryRecord(state, clubId) {
  if (!state || typeof state.position !== 'number') return null;
  const back = typeof state.back === 'string' ? state.back : null;
  if (back && isSetupAssistantPath(back.split(/[?#]/)[0])) return null;
  return {position: state.position, back, clubId};
}

/** How many history entries lie between the current position and the conversation's entry (0 = none, or unknown). */
export function setupUnwindDelta(entry, position) {
  if (!entry || typeof entry.position !== 'number' || typeof position !== 'number') return 0;
  return Math.max(0, position - entry.position);
}

/**
 * Applying leaves no way back into the finished conversation: the browser walks back to the conversation's own history
 * entry (the check page, and any detour from it, drop off the history) and that entry is replaced with the done page, so
 * Back from done is the page the admin was on before the assistant. The replace waits for the walk's popstate (vue-router
 * cancels the half-started navigation to the conversation, which never renders); with no delta it is a plain replace.
 */
export function replaceAfterUnwind(router, delta, to, {timeout = 1000} = {}) {
  if (!(delta > 0) || typeof window === 'undefined') return router.replace(to);
  return new Promise((resolve) => {
    let fired = false;
    const go = () => {
      if (fired) return;
      fired = true;
      window.removeEventListener('popstate', go);
      resolve(router.replace(to));
    };
    window.addEventListener('popstate', go);
    setTimeout(go, timeout);
    router.go(-delta);
  });
}
